import { g, Tool } from '@hcie/core';
import { EventBus } from '@hcie/shared';

/**
 * @file renderer.ts
 * @description Handles the visualization of selections (marching ants).
 */

g.selectionDashOffset = 0;
g.selectionPreviewBorder = []; 

export function drawSelectionBorder(ctx: CanvasRenderingContext2D, offsetX = 0, offsetY = 0) {
    if (!g.isSelectionActive && (!g.selectionPreviewBorder || g.selectionPreviewBorder.length === 0)) return;

    ctx.save();
    ctx.translate(offsetX, offsetY);

    ctx.globalAlpha = 1.0;
    ctx.shadowBlur = 0;
    ctx.filter = "none";
    ctx.lineJoin = "round";
    ctx.lineCap = "round";

    const allPaths = [...(g.selectionBorder || []), ...(g.selectionPreviewBorder || [])];
    if (allPaths.length === 0) {
        ctx.restore();
        return;
    }

    ctx.beginPath();
    allPaths.forEach(path => {
        if (!path || path.length < 2 || !path[0]) return;
        ctx.moveTo(path[0].x, path[0].y);
        for (let i = 1; i < path.length; i++) {
            if (!path[i]) continue;
            ctx.lineTo(path[i].x, path[i].y);
        }
        if (path.length > 2) ctx.closePath();
    });

    // 1. Thick solid black outline (background)
    ctx.setLineDash([]);
    ctx.strokeStyle = "rgba(0, 0, 0, 1.0)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // 2. White dashes on top (foreground)
    ctx.setLineDash([4, 4]);
    ctx.lineDashOffset = -g.selectionDashOffset;
    ctx.strokeStyle = "rgba(255, 255, 255, 1.0)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // 3. Draw Edit Handles if in Ghost Mode
    const isGhostMode = (g as any).settings?.selection?.ghostMode;
    if (!isGhostMode) {
        ctx.restore();
        return;
    }

    const isPointTool = g.current_tool.id === Tool.Lasso?.id || g.current_tool.id === Tool.PolySelect?.id;
    const isRectTool = g.current_tool.id === Tool.RectSelect?.id || g.current_tool.id === Tool.EllipseSelect?.id || g.current_tool.id === (Tool as any).CircleSelect?.id;

    if (isPointTool) {
        const pts = (g.current_tool.id === Tool.PolySelect?.id) ? (window as any).polyPoints : (window as any).lassoPoints;
        const maxPoints = (g as any).settings?.selection?.polygonMaxPoints || 25;
        
        if (pts && pts.length > 0) {
            const step = (g.current_tool.id === Tool.Lasso?.id) ? Math.max(1, Math.floor(pts.length / maxPoints)) : 1;
            ctx.setLineDash([]);
            
            // Draw Node Handles
            for (let i = 0; i < pts.length; i += step) {
                const p = pts[i];
                ctx.fillStyle = '#fff';
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.arc(p.x, p.y, 4 / g.zoom, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();
            }

            // Draw "Curve Handles" (Midpoints for adding nodes) for PolySelect
            if (g.current_tool.id === Tool.PolySelect?.id && pts.length >= 2) {
                for (let i = 0; i < pts.length; i++) {
                    const p1 = pts[i];
                    const p2 = pts[(i + 1) % pts.length];
                    const midX = (p1.x + p2.x) / 2;
                    const midY = (p1.y + p2.y) / 2;
                    
                    ctx.fillStyle = 'rgba(100, 200, 255, 0.8)';
                    ctx.strokeStyle = '#000';
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.rect(midX - 3 / g.zoom, midY - 3 / g.zoom, 6 / g.zoom, 6 / g.zoom);
                    ctx.fill();
                    ctx.stroke();
                }
            }
        }
    } else if (isRectTool && g.isSelectionActive) {
        // Draw Corner Handles for Rect/Ellipse (use _selectionEditBounds for accurate position)
        const b = (g as any)._selectionEditBounds;
        if (!b) { ctx.restore(); return; }
        const { x1, y1, x2, y2 } = b;
        
        const isCircle = g.current_tool.id === (Tool as any).CircleSelect?.id || g.current_tool.id === 'btn-circle-select';
        
        const handles = isCircle ? [
            // Corners (Square boundaries)
            { x: x1, y: y1, cursor: 'nwse-resize', type: 'corner' },
            { x: x2, y: y1, cursor: 'nesw-resize', type: 'corner' },
            { x: x1, y: y2, cursor: 'nesw-resize', type: 'corner' },
            { x: x2, y: y2, cursor: 'nwse-resize', type: 'corner' },
            // Midpoints (Edge centers)
            { x: (x1 + x2) / 2, y: y1, cursor: 'ns-resize', type: 'mid' },
            { x: (x1 + x2) / 2, y: y2, cursor: 'ns-resize', type: 'mid' },
            { x: x1, y: (y1 + y2) / 2, cursor: 'ew-resize', type: 'mid' },
            { x: x2, y: (y1 + y2) / 2, cursor: 'ew-resize', type: 'mid' },
            // Center Point
            { x: (x1 + x2) / 2, y: (y1 + y2) / 2, cursor: 'move', type: 'center' }
        ] : [
            { x: x1, y: y1, cursor: 'nwse-resize' }, { x: x2, y: y1, cursor: 'nesw-resize' },
            { x: x1, y: y2, cursor: 'nesw-resize' }, { x: x2, y: y2, cursor: 'nwse-resize' }
        ];

        ctx.setLineDash([]);
        handles.forEach(p => {
            if (isCircle) {
                if ((p as any).type === 'center') ctx.fillStyle = '#ff3'; // Yellow for center
                else if ((p as any).type === 'corner') ctx.fillStyle = '#f33'; // Red for corners
                else ctx.fillStyle = '#4f4'; // Green for midpoints
            } else {
                ctx.fillStyle = '#fff';
            }
            ctx.strokeStyle = '#33f';
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            if (isCircle && (p as any).type === 'center') {
                ctx.arc(p.x, p.y, 5 / g.zoom, 0, Math.PI * 2);
            } else {
                ctx.rect(p.x - 4 / g.zoom, p.y - 4 / g.zoom, 8 / g.zoom, 8 / g.zoom);
            }
            ctx.fill();
            ctx.stroke();
        });
    }

    ctx.restore();
}

// Animation loop for marching ants
let antsInterval: any = null;

export function startAntsAnimation() {
    if (antsInterval) return;
    antsInterval = setInterval(() => {
        if (g.isSelectionActive || (g.selectionPreviewBorder && g.selectionPreviewBorder.length > 0)) {
            g.selectionDashOffset = (g.selectionDashOffset + 0.5) % 8;
            EventBus.emit('RENDER_LAYERS');
        }
    }, 40);
}

export function stopAntsAnimation() {
    if (antsInterval) {
        clearInterval(antsInterval);
        antsInterval = null;
    }
}
